import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  position?: 'right' | 'left' | 'top' | 'bottom';
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, position = 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateTooltipPosition = () => {
      if (!isVisible || !tooltipRef.current || !containerRef.current) return;

      const container = containerRef.current.getBoundingClientRect();
      const tooltip = tooltipRef.current;

      switch (position) {
        case 'right':
          tooltip.style.left = `${container.right + 8}px`;
          tooltip.style.top = `${container.top + (container.height / 2)}px`;
          tooltip.style.transform = 'translateY(-50%)';
          break;
        case 'left':
          tooltip.style.right = `${window.innerWidth - container.left + 8}px`;
          tooltip.style.top = `${container.top + (container.height / 2)}px`;
          tooltip.style.transform = 'translateY(-50%)';
          break;
        case 'top':
          tooltip.style.bottom = `${window.innerHeight - container.top + 8}px`;
          tooltip.style.left = `${container.left + (container.width / 2)}px`;
          tooltip.style.transform = 'translateX(-50%)';
          break;
        case 'bottom':
          tooltip.style.top = `${container.bottom + 8}px`;
          tooltip.style.left = `${container.left + (container.width / 2)}px`;
          tooltip.style.transform = 'translateX(-50%)';
          break;
      }
    };

    updateTooltipPosition();
    window.addEventListener('scroll', updateTooltipPosition);
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('scroll', updateTooltipPosition);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [isVisible, position]);

  return (
    <div
      ref={containerRef}
      style={{ 
        position: 'relative', 
        display: 'inline-block',
      }}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed', // Changed from absolute to fixed
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            zIndex: 99999,
            pointerEvents: 'none',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

export default Tooltip;